import { or, rankWith, scopeEndIs } from '@jsonforms/core';
import type { ControlProps } from '@jsonforms/core';
import { withJsonFormsControlProps, useJsonForms } from '@jsonforms/react';
import { cn } from '@thinkalpha/ui-core/utils';
import { Label, Description, Errors } from 'src/components/JsonForm/helpers';
import { JsonFormErrorFloatingPane } from 'src/components/JsonForm/JsonFormErrorFloatingPane';
import {
    defaultPriceStep,
    defaultPricePrecision,
    defaultPricePrecisionBelowADollar,
    defaultPriceStepBelowADollar,
    fakeRequired,
    lightStyle,
    pricePrecisionBelowADollarByInstrumentType,
    priceStepBelowADollarByInstrumentType,
} from 'src/components/JsonForm/ThinkalphaTheme';
import type { InstrumentType } from 'src/components/JsonForm/ThinkalphaTheme';
import { MaskedNumberStepper } from 'src/components/ui/input/masked/MaskedNumberStepper';
import { useNumericInputState } from 'src/hooks/useNumericInputState';

export const PriceControlComponent = ({
    id,
    data,
    path,
    description,
    errors,
    label,
    required,
    visible,
    enabled,
    handleChange,
}: ControlProps) => {
    const ctx = useJsonForms();
    const formData: { [key: string]: any } = ctx?.core?.data;
    const instrumentType = (formData?.instrument?.type as InstrumentType) ?? 'equity';

    const hasErrors = errors.length > 0;

    const { displayValue: stringValue, handleChange: onChange } = useNumericInputState(
        data,
        (value) => handleChange(path, value),
        0,
    );

    // Calculate precision based on stringValue (what user is typing) not just data.
    // This allows users to type negative sub-dollar values like -0.005 starting from 0.
    const numericStringValue = stringValue ? Number(stringValue) : 0;
    const isBelowDollar = numericStringValue !== 0 ? Math.abs(numericStringValue) < 1 : stringValue.includes('.');
    const precision = isBelowDollar
        ? (pricePrecisionBelowADollarByInstrumentType[instrumentType] ?? defaultPricePrecisionBelowADollar)
        : defaultPricePrecision;
    const step = isBelowDollar
        ? (priceStepBelowADollarByInstrumentType[instrumentType] ?? defaultPriceStepBelowADollar)
        : defaultPriceStep;

    const isRequired = fakeRequired.includes(path) || required;

    const isLight = lightStyle.includes(path);

    const variant = hasErrors ? (isLight ? 'errorLight' : 'errorDefault') : isLight ? 'light' : 'default';

    return visible ? (
        <div>
            <div className="flex items-center gap-2">
                <Label label={label} required={isRequired} htmlFor={id} />
                <Description description={description} />
            </div>
            <JsonFormErrorFloatingPane hasErrors={hasErrors} errors={<Errors errors={errors} />}>
                <MaskedNumberStepper
                    id={id}
                    value={stringValue}
                    onChange={onChange}
                    decimalsLimit={precision}
                    decimalScale={precision}
                    step={step}
                    allowNegativeValue={true}
                    prefix="$"
                    dimensions="sm"
                    variant={variant}
                    inputClassName={cn(!enabled && 'disabled:text-black')}
                />
            </JsonFormErrorFloatingPane>
        </div>
    ) : null;
};

export const PriceControlTester = rankWith(
    10, //increase rank as needed
    or(scopeEndIs('price'), scopeEndIs('stopPrice')),
);

export const PriceControl = withJsonFormsControlProps(PriceControlComponent);
